import { Button, Cell, Popover, Row, Typography } from "@hellocarbo/lasagne";
import { ArrowRight, Edit2, MoreHorizontal, Trash } from "lucide-react";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigation } from "react-router";

import { PortalContainerContext } from "@/contexts";
import { TScenario, TSimulation } from "@/types";
import { formatCarbon } from "@/utils/carbon";
import { ScenarioIcons } from "@/utils/scenario";

type SimulationRowProps = {
  deleteSimulation: () => void;
  simulation: TSimulation;
};

function SimulationRow({ simulation, deleteSimulation }: SimulationRowProps) {
  const { t } = useTranslation(["formatter", "common", "simulation"]);
  const { container } = useContext(PortalContainerContext);

  const { formattedCarbon, unit } = formatCarbon(t, simulation.carbon_tons);

  const Icon = simulation.scenario
    ? ScenarioIcons[simulation.scenario]
    : ScenarioIcons[TScenario.free];

  const navigation = useNavigation();

  return (
    <Row>
      <Cell className="gap-x-md flex items-center">
        <Icon className="size-md text-primary-light" />
        <div className="gap-y-3xs flex flex-col">
          <Typography as="h2" weight="semibold">
            {simulation.name}
          </Typography>
          <Typography step="-1" weight="semibold" className="text-grayscale">
            {t("formatter:date", {
              value: new Date(simulation.period_start),
              formatParams: {
                value: { year: "numeric", month: "long", day: "numeric" },
              },
            })}
            &nbsp;-&nbsp;
            {t("formatter:date", {
              value: new Date(simulation.period_end),
              formatParams: {
                value: { year: "numeric", month: "long", day: "numeric" },
              },
            })}
          </Typography>
        </div>
      </Cell>
      <Cell>
        <Typography step="-1" weight="semibold">
          {t("formatter:number", { value: simulation.employees })}
        </Typography>
      </Cell>
      <Cell>
        <Typography step="-1" weight="semibold">
          {formattedCarbon}
          &nbsp;
          {t(`common:unit.${unit}`)}
        </Typography>
      </Cell>
      <Cell className="flex items-center justify-end">
        <Popover
          trigger={<Button shape="link" Icon={MoreHorizontal} />}
          portalContainer={container as Element}
          side="left"
          align="start"
        >
          <div className="gap-y-2xs flex flex-col">
            <Button
              as={Link}
              to={`/simulations/${simulation.id}/parameters`}
              shape="link"
              size="small"
              className="text-left"
            >
              <Edit2 className="size-sm" />
              {t("simulation:modifyParameters")}
            </Button>
            <Button
              shape="link"
              intent="negative"
              size="small"
              className="text-left"
              onClick={deleteSimulation}
              isLoading={navigation.state === "submitting"}
            >
              <Trash className="size-sm" />
              {t("simulation:deleteSimulation")}
            </Button>
          </div>
        </Popover>
        <Button
          as={Link}
          to={simulation.details_url}
          reloadDocument
          size="small"
          shape="soft"
          Icon={ArrowRight}
        />
      </Cell>
    </Row>
  );
}

export default SimulationRow;
